import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { CampoControlado, Entrada, EntradaSensivel, Rotulo, Selecao, type Opcao } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { TextoCortado } from '../../componentes/texto-cortado';
import { dinheiro } from '../../nucleo/formato';
import { RECORRENCIAS_ITEM, totalDaLinha, type ItemContrato, type RecorrenciaItem } from './tipos';

/** Serviço do catálogo como a lista de apoio devolve. */
export interface ServicoOpcao {
  id: string;
  nome: string;
  preco_venda_centavos: number;
  custo_centavos: number;
  categoria_padrao_id: string | null;
  centro_custo_padrao_id: string | null;
}

/** Linha em edição: a quantidade fica em texto para aceitar "1,5" enquanto se digita. */
export interface ItemForm {
  chave: string;
  servico_id: string;
  descricao: string;
  quantidade: string;
  preco: number | null;
  custo: number | null;
  categoria_id: string;
  centro_custo_id: string;
  /** 22/10: vazia = a do contrato. */
  recorrencia: RecorrenciaItem | '';
}

export const novoItem = (centro = ''): ItemForm => ({ chave: crypto.randomUUID(), servico_id: '', descricao: '', quantidade: '1', preco: null, custo: null, categoria_id: '', centro_custo_id: centro, recorrencia: '' });

export const itemDoContrato = (i: ItemContrato): ItemForm => ({
  chave: crypto.randomUUID(),
  servico_id: i.servico_id ?? '',
  descricao: i.descricao,
  quantidade: String(i.quantidade).replace('.', ','),
  preco: i.preco_centavos,
  custo: i.custo_centavos,
  categoria_id: i.categoria_id ?? '',
  centro_custo_id: i.centro_custo_id ?? '',
  recorrencia: i.recorrencia ?? '',
});

export const quantidadeDe = (q: string) => {
  const n = Number(q.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/** Itens no formato da API; lança Error com a mensagem pronta quando uma linha está incompleta. */
export function itensParaApi(itens: ItemForm[], incompleto: string) {
  return itens
    .filter((i) => i.descricao.trim() || i.preco !== null || i.servico_id)
    .map((i) => {
      const q = quantidadeDe(i.quantidade);
      if (!i.descricao.trim() || i.preco === null || i.preco < 0 || q <= 0) throw new Error(incompleto);
      return {
        servico_id: i.servico_id || null,
        descricao: i.descricao.trim(),
        quantidade: q,
        preco_centavos: i.preco,
        custo_centavos: i.custo ?? 0,
        categoria_id: i.categoria_id || null,
        centro_custo_id: i.centro_custo_id || null,
        // 22/10: só com recorrência própria (a lista de itens troca inteira; sem ela, a do contrato).
        ...(i.recorrencia ? { recorrencia: i.recorrencia } : {}),
      };
    });
}

/**
 * Editor dos itens do contrato (W12.3): serviço do catálogo (preenche descrição, preço, custo e os padrões do Financeiro,
 * tudo editável), quantidade, preço, custo, categoria de receita e centro de custo.
 * C4: fica na área "Serviços do contrato" (o título da área é o do grupo do formulário; a legenda fica para o leitor de
 * tela) e o centro de custo do contrato é o padrão do item novo e do serviço escolhido sem centro.
 * 22/10 (contrato por vertical): com `comRecorrencia`, a recorrência de cada item (vazia = a do contrato).
 */
export function ItensContrato({
  itens,
  aoMudar,
  servicos,
  categorias,
  centros,
  centroPadrao = '',
  moeda,
  desabilitado,
  semCusto = false,
  comRecorrencia = false,
}: {
  itens: ItemForm[];
  aoMudar: (v: ItemForm[]) => void;
  servicos: ServicoOpcao[];
  categorias: Opcao[] | null;
  centros: Opcao[] | null;
  /** C4: centro de custo do contrato. */
  centroPadrao?: string;
  moeda: string;
  desabilitado?: boolean;
  /** Pacote 2: o item do plano não tem custo (o custo é do contrato que nasce dele). */
  semCusto?: boolean;
  /** 22/10: escolhe a recorrência por item (contrato; o plano tem a dele). */
  comRecorrencia?: boolean;
}) {
  const { t } = useTranslation('assinaturas');
  const muda = (chave: string, parcial: Partial<ItemForm>) => aoMudar(itens.map((i) => (i.chave === chave ? { ...i, ...parcial } : i)));
  const escolherServico = (i: ItemForm, id: string) => {
    const sv = servicos.find((s) => s.id === id);
    if (!sv) return muda(i.chave, { servico_id: '' });
    muda(i.chave, {
      servico_id: id,
      descricao: i.descricao.trim() ? i.descricao : sv.nome,
      preco: i.preco ?? sv.preco_venda_centavos,
      custo: i.custo ?? sv.custo_centavos,
      categoria_id: i.categoria_id || sv.categoria_padrao_id || '',
      centro_custo_id: i.centro_custo_id || centroPadrao || sv.centro_custo_padrao_id || '',
    });
  };
  return (
    <fieldset className="flex min-w-0 flex-col gap-3 sm:col-span-6" disabled={desabilitado}>
      <legend className="sr-only">{t('itens.titulo')}</legend>
      {!itens.length && <p className="text-sm text-tinta-3">{t('itens.vazio')}</p>}
      {itens.map((i, n) => (
        <div key={i.chave} className="grid min-w-0 grid-cols-6 gap-2 rounded-lg border border-borda bg-sup-1 p-3">
          <div className="col-span-6">
            <Rotulo id={`item-servico-${i.chave}`} rotulo={t('itens.servico')}>
              <Selecao id={`item-servico-${i.chave}`} vazio={t('itens.semServico')} opcoes={servicos.map((s) => ({ valor: s.id, rotulo: s.nome }))} value={i.servico_id} onChange={(e) => escolherServico(i, e.target.value)} />
            </Rotulo>
          </div>
          <div className="col-span-6">
            <Rotulo id={`item-desc-${i.chave}`} rotulo={t('itens.descricao', { n: n + 1 })}>
              <EntradaSensivel mascara={null} livre id={`item-desc-${i.chave}`} maxLength={300} value={i.descricao} onChange={(e) => muda(i.chave, { descricao: e.target.value })} />
            </Rotulo>
          </div>
          <div className="col-span-2">
            <Rotulo id={`item-qtd-${i.chave}`} rotulo={t('itens.quantidade')}>
              <Entrada id={`item-qtd-${i.chave}`} inputMode="decimal" className="numero text-right" value={i.quantidade} onChange={(e) => muda(i.chave, { quantidade: e.target.value })} />
            </Rotulo>
          </div>
          <div className="col-span-4 sm:col-span-2">
            <CampoControlado def={{ nome: `preco-${i.chave}`, rotulo: t('itens.preco'), tipo: 'dinheiro' }} valor={i.preco} aoMudar={(v) => muda(i.chave, { preco: v as number | null })} />
          </div>
          {!semCusto && (
            <div className="col-span-3 sm:col-span-2">
              <CampoControlado def={{ nome: `custo-${i.chave}`, rotulo: t('itens.custo'), tipo: 'dinheiro' }} valor={i.custo} aoMudar={(v) => muda(i.chave, { custo: v as number | null })} />
            </div>
          )}
          <div className={`flex min-w-0 flex-col justify-end gap-1 ${semCusto ? 'col-span-5 sm:col-span-1' : 'col-span-2 sm:col-span-5'}`}>
            <span className="text-sm font-medium text-tinta-2">{t('itens.total')}</span>
            <TextoCortado className="numero flex h-10 items-center text-sm text-tinta-1">{anunciarOcultos(dinheiro(totalDaLinha(quantidadeDe(i.quantidade), i.preco ?? 0), moeda))}</TextoCortado>
          </div>
          <div className="col-span-1 flex items-end justify-end">
            <Botao variante="fantasma" aria-label={t('itens.remover')} title={t('itens.remover')} onClick={() => aoMudar(itens.filter((x) => x.chave !== i.chave))} icone={<Trash2 className="size-4" />} />
          </div>
          {comRecorrencia && (
            <div className="col-span-6 sm:col-span-3">
              <Rotulo id={`item-rec-${i.chave}`} rotulo={t('itens.recorrencia')}>
                <Selecao
                  id={`item-rec-${i.chave}`}
                  vazio={t('itens.daContrato')}
                  opcoes={RECORRENCIAS_ITEM.map((r) => ({ valor: r, rotulo: t(`recorrenciaItem.${r}`) }))}
                  value={i.recorrencia}
                  onChange={(e) => muda(i.chave, { recorrencia: e.target.value as RecorrenciaItem | '' })}
                />
              </Rotulo>
            </div>
          )}
          {categorias && (
            <div className="col-span-6 sm:col-span-3">
              <Rotulo id={`item-cat-${i.chave}`} rotulo={t('itens.categoria')}>
                <Selecao id={`item-cat-${i.chave}`} vazio={t('itens.semCategoria')} opcoes={categorias} value={i.categoria_id} onChange={(e) => muda(i.chave, { categoria_id: e.target.value })} />
              </Rotulo>
            </div>
          )}
          {centros && (
            <div className="col-span-6 sm:col-span-3">
              <Rotulo id={`item-cc-${i.chave}`} rotulo={t('itens.centro')}>
                <Selecao id={`item-cc-${i.chave}`} vazio={t('itens.semCentro')} opcoes={centros} value={i.centro_custo_id} onChange={(e) => muda(i.chave, { centro_custo_id: e.target.value })} />
              </Rotulo>
            </div>
          )}
        </div>
      ))}
      <div>
        <Botao icone={<Plus className="size-4" />} onClick={() => aoMudar([...itens, novoItem(centroPadrao)])}>
          {t('itens.adicionar')}
        </Botao>
      </div>
    </fieldset>
  );
}
